import { unwrap } from "@sagot/api-client";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { engageKeys } from "../shared/engage-keys";
import type { IntegrationKey, LeadFilters, LeadPatch, StageIn } from "./lead-types";

const PAGE_SIZE = 25;

function useLeadKeys() {
  const wsId = useWorkspaceId();
  const root = engageKeys.leads(wsId);
  return {
    root,
    stages: [...root, "stages"] as const,
    stats: [...root, "stats"] as const,
    list: (filters: LeadFilters) => [...root, "list", filters] as const,
    board: (q: string) => [...root, "board", q] as const,
    detail: (id: string) => [...root, "detail", id] as const,
    integrations: [...root, "integrations"] as const,
  };
}

/** `GET /v1/lead-stages`: the tenant pipeline (names are tenant data). */
export function useStages() {
  const api = useApi();
  const keys = useLeadKeys();
  return useQuery({ queryKey: keys.stages, queryFn: () => unwrap(api.GET("/v1/lead-stages")) });
}

export function useLeadStats() {
  const api = useApi();
  const keys = useLeadKeys();
  return useQuery({ queryKey: keys.stats, queryFn: () => unwrap(api.GET("/v1/leads/stats")) });
}

/** `GET /v1/leads` with cursor pagination ("Load more"). */
export function useLeadList(filters: LeadFilters, enabled: boolean) {
  const api = useApi();
  const keys = useLeadKeys();
  return useInfiniteQuery({
    queryKey: keys.list(filters),
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/v1/leads", {
          params: { query: { stage_id: filters.stageId, q: filters.q || null, interest: filters.interest, cursor: pageParam, limit: PAGE_SIZE } },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor,
  });
}

export function useLeadBoard(q: string, enabled: boolean) {
  const api = useApi();
  const keys = useLeadKeys();
  return useQuery({ queryKey: keys.board(q), enabled, queryFn: () => unwrap(api.GET("/v1/leads/board", { params: { query: { q: q || null } } })) });
}

export function useLeadDetail(leadId: string | null) {
  const api = useApi();
  const keys = useLeadKeys();
  return useQuery({
    queryKey: keys.detail(leadId ?? ""),
    enabled: leadId !== null,
    queryFn: () => unwrap(api.GET("/v1/leads/{lead_id}", { params: { path: { lead_id: leadId ?? "" } } })),
  });
}

export function useIntegrations() {
  const api = useApi();
  const keys = useLeadKeys();
  return useQuery({ queryKey: keys.integrations, queryFn: () => unwrap(api.GET("/v1/integrations")), staleTime: 60_000 });
}

function useInvalidateLeads() {
  const queryClient = useQueryClient();
  const keys = useLeadKeys();
  return () => queryClient.invalidateQueries({ queryKey: keys.root });
}

export function usePatchLead() {
  const api = useApi();
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: LeadPatch }) => unwrap(api.PATCH("/v1/leads/{lead_id}", { params: { path: { lead_id: id } }, body: { clear_owner: false, ...patch } })),
    onSettled: invalidate,
  });
}

export function useAddNote(leadId: string) {
  const api = useApi();
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: (body: string) => unwrap(api.POST("/v1/leads/{lead_id}/activities", { params: { path: { lead_id: leadId } }, body: { type: "note", body } })),
    onSuccess: invalidate,
  });
}

export function useRegenerateSummary(leadId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const keys = useLeadKeys();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/leads/{lead_id}/summary", { params: { path: { lead_id: leadId }, query: { refresh: true } } })),
    onSuccess: (detail) => {
      queryClient.setQueryData(keys.detail(leadId), detail);
    },
  });
}

export function useSyncLeads() {
  const api = useApi();
  const invalidate = useInvalidateLeads();
  return useMutation({ mutationFn: (provider: IntegrationKey) => unwrap(api.POST("/v1/leads/sync", { body: { provider } })), onSuccess: invalidate });
}

export function useSaveStages() {
  const api = useApi();
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: (body: { stages: StageIn[]; reassign: Record<string, string> }) => unwrap(api.PUT("/v1/lead-stages", { body })),
    onSuccess: invalidate,
  });
}

export function useExportLeads() {
  const api = useApi();
  return useMutation({
    mutationFn: (stageId: string | null) => unwrap(api.GET("/v1/leads/export", { params: { query: { stage_id: stageId } }, parseAs: "blob" })),
  });
}
